import { Contract } from '../../models/Contract'
import { Signature } from '../../models/Signature'
import { User } from '../../models/User'
import { API_BASE } from '../../utils/constants'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { RecordStatusChecklist } from '../common/RecordStatusChecklist'
import { BaseRecordView } from '../common/sections'
import { ContactAvatar } from '../contacts/ContactAvatar'

type Props = {
    signature: Signature
    contract: Contract
    user: User
}

export const SignatureView = ({ signature, contract, user }: Props) => {
    return (
        <BaseRecordView.Container>
            <BaseRecordView.LeftColumn>
                <Card size="sm">
                    <div className="flex flex-col items-center justify-center">
                        <Icon
                            icon="pen.large"
                            background="round"
                            className="size-[116px]"
                            svgClassName="size-[56px]"
                        />

                        <Heading size="sm" className="mt-6 mb-1 text-center">
                            {contract.name}
                        </Heading>

                        {signature.signedDate !== undefined && (
                            <Paragraph size="lg" className="mb-1 font-normal text-gray-600">
                                Signed on {signature.signedDate.format('MMM D, YYYY h:mm a')}
                            </Paragraph>
                        )}

                        <Paragraph size="lg" className="mb-1 font-normal text-gray-600">
                            {contract.refnum}
                        </Paragraph>

                        <Paragraph size="lg" className="mb-1 font-normal text-gray-600">
                            {signature.ipAddr}
                        </Paragraph>
                    </div>

                    <Divider className="my-6" />

                    <div className="flex flex-col items-stretch justify-start gap-y-6">
                        {!!signature.subaccount && (
                            <BaseRecordView.IconField
                                icon={
                                    <ContactAvatar
                                        user={signature.subaccount}
                                        containerClassName="row-span-2"
                                        hasHoverEffect
                                    />
                                }
                                title={signature.subaccount.name ?? ''}
                                subtitle={signature.subaccount.labelString}
                                href={`/subaccounts/${signature.subaccount.id}`}
                            />
                        )}

                        <BaseRecordView.IconField
                            icon={<ContactAvatar user={user} hasHoverEffect />}
                            title={user.name ?? ''}
                            subtitle="Client"
                            secondaryView={
                                <a href={`/messages/${user.id}`}>
                                    <Button intent="secondary" size="small">
                                        Chat
                                    </Button>
                                </a>
                            }
                            href={`/clients/${user.id}`}
                        />
                    </div>
                </Card>

                <Card size="sm" title="Status">
                    <RecordStatusChecklist
                        items={signature.statuses.map((status) => ({
                            title: status.title,
                            subtitle: status.date?.format('MMM Do, YYYY [at] h:mm A'),
                            status: status.date !== undefined ? 'checked' : 'empty',
                        }))}
                        className="mt-6"
                    />
                </Card>
            </BaseRecordView.LeftColumn>

            <BaseRecordView.RightColumn>
                <Card title="Preview">
                    <iframe
                        src={`${API_BASE}/signature/${signature.id}/print`}
                        className="mt-8 h-[722px] w-full"
                    />
                </Card>
            </BaseRecordView.RightColumn>
        </BaseRecordView.Container>
    )
}
